import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REGISTRY_API = `${BACKEND_URL}/api/connection-registry`;

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    withCredentials: true,
    headers: { Authorization: `Bearer ${token}` },
  };
}

// Supporting Tools and Supporting Tools Live keep separate connections: 'standard'
// or 'live' picks which set a call reads or writes.
const scoped = (scope) => ({ ...authHeaders(), params: { scope: scope || 'standard' } });

export async function fetchEntries(scope) {
  const res = await axios.get(REGISTRY_API, scoped(scope));
  return res.data;
}

export async function fetchEntry(connectionId) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}`, authHeaders());
  return res.data;
}

export async function fetchProducts(scope) {
  const res = await axios.get(`${REGISTRY_API}/products`, scoped(scope));
  return res.data;
}

export async function fetchClientsForProduct(productKey, scope) {
  const res = await axios.get(`${REGISTRY_API}/products/${encodeURIComponent(productKey)}/clients`, scoped(scope));
  return res.data;
}

export async function createEntry(body, scope) {
  const res = await axios.post(REGISTRY_API, body, scoped(scope));
  return res.data;
}

export async function updateEntry(connectionId, body) {
  const res = await axios.put(`${REGISTRY_API}/${encodeURIComponent(connectionId)}`, body, authHeaders());
  return res.data;
}

export async function deleteEntry(connectionId) {
  const res = await axios.delete(`${REGISTRY_API}/${encodeURIComponent(connectionId)}`, authHeaders());
  return res.data;
}

export async function connectEntry(connectionId, slot = 'primary') {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/connect`,
    {},
    { ...authHeaders(), params: { slot } },
  );
  return res.data;
}

/** Test Connection1 or Connection2 from form fields (draft or saved password). */
export async function testDbSlot(body) {
  const res = await axios.post(`${REGISTRY_API}/test-db`, body, authHeaders());
  return res.data;
}

// --- EasyChit proxy (Name Change / Mobile Number Change) ---

export async function fetchBranches(connectionId) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/branches`, authHeaders());
  return res.data;
}

export async function fetchCompanies(connectionId) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/companies`, authHeaders());
  return res.data;
}

export async function fetchGroups(connectionId, branchCode) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/groups`, {
    ...authHeaders(), params: { branch_code: branchCode },
  });
  return res.data;
}

export async function fetchTickets(connectionId, branchCode, groupCode) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/tickets`, {
    ...authHeaders(), params: { branch_code: branchCode, group_code: groupCode },
  });
  return res.data;
}

export async function fetchRecord(connectionId, branchCode, ticketNo, chitgroupId) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/record`, {
    ...authHeaders(), params: { branch_code: branchCode, ticket_no: ticketNo, chitgroup_id: chitgroupId },
  });
  return res.data;
}

export async function submitNameChange(connectionId, {
  contactId, branchCode, oldName, oldSurname, oldMailingName, newName, newSurname, reason,
}) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/name-change`,
    {
      contact_id: contactId, branch_code: branchCode,
      old_name: oldName, old_surname: oldSurname, old_mailing_name: oldMailingName,
      new_name: newName, new_surname: newSurname, reason,
    },
    authHeaders(),
  );
  return res.data;
}

export async function submitMobileChange(connectionId, { contactId, branchCode, oldMobileNo, newMobileNo, reason }) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/mobile-change`,
    {
      contact_id: contactId, branch_code: branchCode,
      old_mobile_no: oldMobileNo, new_mobile_no: Number(newMobileNo), reason,
    },
    authHeaders(),
  );
  return res.data;
}

// --- Auction Date Time ---

export async function fetchAuctionSchedule(connectionId, branchCode, chitgroupId) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/auction-schedule`, {
    ...authHeaders(), params: { branch_code: branchCode, chitgroup_id: chitgroupId },
  });
  return res.data;
}

export async function submitAuctionSchedule(connectionId, {
  branchCode, chitgroupId, oldAuctionDate, oldAuctionTime, newAuctionDate, newAuctionTime, reason,
}) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/auction-schedule`,
    {
      branch_code: branchCode, chitgroup_id: chitgroupId,
      old_auction_date: oldAuctionDate, old_auction_time: oldAuctionTime,
      new_auction_date: newAuctionDate, new_auction_time: newAuctionTime, reason,
    },
    authHeaders(),
  );
  return res.data;
}

// --- Termination Date And Commence Date ---

export async function fetchDatesChange(connectionId, branchCode, groupCode) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/dates-change`, {
    ...authHeaders(), params: { branch_code: branchCode, group_code: groupCode },
  });
  return res.data;
}

export async function submitDatesChange(connectionId, {
  branchCode, groupCode, noOfAuctions, psoNumber, commencementDate, terminationDate, certificateNo, reason,
}) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/dates-change`,
    {
      branch_code: branchCode, group_code: groupCode, no_of_auctions: noOfAuctions,
      pso_number: psoNumber, commencement_date: commencementDate, termination_date: terminationDate,
      commencement_certificate_no: certificateNo, reason,
    },
    authHeaders(),
  );
  return res.data;
}

// --- BPO Guarantor Id Delete ---

export async function fetchBpoGuarantors(connectionId, branchCode, groupCode, ticketNo) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/guarantors-bpo`, {
    ...authHeaders(), params: { branch_code: branchCode, group_code: groupCode, ticket_no: ticketNo },
  });
  return res.data;
}

export async function submitDeleteBpoGuarantors(connectionId, { branchCode, chitgroupId, ticketNo, guarantors, reason }) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/guarantors-bpo/delete`,
    { branch_code: branchCode, chitgroup_id: chitgroupId, ticket_no: ticketNo, guarantors, reason },
    authHeaders(),
  );
  return res.data;
}

// --- Cheque Issue ---

export async function fetchChequeNumbers(connectionId, branchCode) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/cheque-numbers`, {
    ...authHeaders(), params: { branch_code: branchCode },
  });
  return res.data;
}

export async function fetchChequeDetails(connectionId, branchCode, paymentNumber, referenceNumber, clearStatus) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/cheque-details`, {
    ...authHeaders(),
    params: {
      branch_code: branchCode, payment_number: paymentNumber,
      reference_number: referenceNumber, clear_status: clearStatus,
    },
  });
  return res.data;
}

export async function submitChequeIssueClear(connectionId, {
  branchCode, paymentNumber, referenceNumber, clearStatus, transactionNo, bankEntriesId, reason,
}) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/cheque-issue/clear`,
    {
      branch_code: branchCode, payment_number: paymentNumber, reference_number: referenceNumber,
      clear_status: clearStatus, transaction_no: transactionNo, bank_entries_id: bankEntriesId, reason,
    },
    authHeaders(),
  );
  return res.data;
}

// --- Cheque Return Charges Permission ---

export async function fetchChequeReturnCharges(connectionId, companyCode) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/cheque-return-charges`, {
    ...authHeaders(), params: { company_code: companyCode },
  });
  return res.data;
}

// Company code and reason are not sent here — they go in the SaveData audit entry.
// allowed: true = Allowed(0/-) (no charge), false = NotAllowed(250/-).
export async function submitChequeReturnCharges(connectionId, { configId, currentAmount, allowed }) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/cheque-return-charges`,
    { config_id: configId, current_amount: currentAmount, allowed },
    authHeaders(),
  );
  return res.data;
}

// --- Deposited Cheque ---

export async function fetchDepositedCheques(connectionId, branchCode) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/deposited-cheques`, {
    ...authHeaders(), params: { branch_code: branchCode },
  });
  return res.data;
}

export async function fetchDepositedChequeDetails(connectionId, branchCode, generalReceiptNumber) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/deposited-cheques/details`, {
    ...authHeaders(), params: { branch_code: branchCode, general_receipt_number: generalReceiptNumber },
  });
  return res.data;
}

export async function fetchDepositedChequeLive(connectionId, branchCode, generalReceiptNumber, interbranchId, referenceNumber) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/deposited-cheques/live`, {
    ...authHeaders(),
    params: {
      branch_code: branchCode, general_receipt_number: generalReceiptNumber,
      interbranch_id: interbranchId, reference_number: referenceNumber,
    },
  });
  return res.data;
}

export async function submitReverseDepositedCheque(connectionId, {
  branchCode, caoSchema, chitgroupId, ticketNo, contactId, generalReceiptNumber,
  referenceNumber, receiptNumber, bankEntriesId, transactionNo, reason,
}) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/deposited-cheques/reverse`,
    {
      branch_code: branchCode, cao_schema: caoSchema, chitgroup_id: chitgroupId, ticket_no: ticketNo,
      contact_id: contactId, general_receipt_number: generalReceiptNumber, reference_number: referenceNumber,
      receipt_number: receiptNumber, bank_entries_id: bankEntriesId, transaction_no: transactionNo, reason,
    },
    authHeaders(),
  );
  return res.data;
}

// --- General Receipt Cancel ---

export async function fetchGeneralReceipts(connectionId, branchCode) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/general-receipts`, {
    ...authHeaders(), params: { branch_code: branchCode },
  });
  return res.data;
}

export async function fetchGeneralReceiptDetails(connectionId, branchCode, generalReceiptNumber, interbranchId) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/general-receipts/details`, {
    ...authHeaders(),
    params: { branch_code: branchCode, general_receipt_number: generalReceiptNumber, interbranch_id: interbranchId },
  });
  return res.data;
}

export async function fetchGeneralReceiptAmount(connectionId, branchCode, depositedReferenceNo) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/general-receipts/amount`, {
    ...authHeaders(), params: { branch_code: branchCode, deposited_reference_no: depositedReferenceNo },
  });
  return res.data;
}

export async function fetchGeneralReceiptVoucherTransactions(connectionId, branchCode, generalReceiptNumber, contactId) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/general-receipts/voucher-transactions`, {
    ...authHeaders(),
    params: { branch_code: branchCode, general_receipt_number: generalReceiptNumber, contact_id: contactId },
  });
  return res.data;
}

export async function submitReverseGeneralReceipt(connectionId, {
  branchCode, caoSchema, groupCode, ticketNo, contactId, generalReceiptNumber,
  commonReceiptNumber, paymentNumber, paymentDate, paymentVoucherId, paymentVoucherDetailsId, reason,
}) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/general-receipts/reverse`,
    {
      branch_code: branchCode, cao_schema: caoSchema, group_code: groupCode, ticket_no: ticketNo,
      contact_id: contactId, general_receipt_number: generalReceiptNumber,
      common_receipt_number: commonReceiptNumber, payment_number: paymentNumber, payment_date: paymentDate,
      payment_voucher_id: paymentVoucherId, payment_voucher_details_id: paymentVoucherDetailsId, reason,
    },
    authHeaders(),
  );
  return res.data;
}

// --- MV Cancel ---

export async function fetchMvNumbers(connectionId, branchCode) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/mv-numbers`, {
    ...authHeaders(), params: { branch_code: branchCode },
  });
  return res.data;
}

export async function fetchMvDetails(connectionId, branchCode, paymentNumber) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/mv-numbers/details`, {
    ...authHeaders(), params: { branch_code: branchCode, payment_number: paymentNumber },
  });
  return res.data;
}

export async function fetchMvLineItems(connectionId, branchCode, paymentNumber, paymentVoucherId) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/mv-numbers/line-items`, {
    ...authHeaders(),
    params: { branch_code: branchCode, payment_number: paymentNumber, payment_voucher_id: paymentVoucherId },
  });
  return res.data;
}

export async function submitReverseMvVoucher(connectionId, {
  branchCode, paymentNumber, paymentDate, paymentVoucherId, contactId, paymentVoucherDetailsId, reason,
}) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/mv-numbers/reverse`,
    {
      branch_code: branchCode, payment_number: paymentNumber, payment_date: paymentDate,
      payment_voucher_id: paymentVoucherId, contact_id: contactId,
      payment_voucher_details_id: paymentVoucherDetailsId, reason,
    },
    authHeaders(),
  );
  return res.data;
}

// --- PAN Change ---

export async function fetchPanNumbers(connectionId) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/pan-numbers`, authHeaders());
  return res.data;
}

export async function fetchPanContactDocuments(connectionId, panNumber) {
  const res = await axios.get(`${REGISTRY_API}/${encodeURIComponent(connectionId)}/pan-numbers/contact-documents`, {
    ...authHeaders(), params: { pan_number: panNumber },
  });
  return res.data;
}

export async function submitPanChange(connectionId, {
  branchCode, oldPanNumber, newPanNumber, contactReferenceId, contactId, documentIds, reason,
}) {
  const res = await axios.post(
    `${REGISTRY_API}/${encodeURIComponent(connectionId)}/pan-numbers/update`,
    {
      branch_code: branchCode, old_pan_number: oldPanNumber, new_pan_number: newPanNumber,
      contact_reference_id: contactReferenceId, contact_id: contactId, document_ids: documentIds, reason,
    },
    authHeaders(),
  );
  return res.data;
}

// // # The Supporting Tools forms call EasyChit /api/Change/* helpers below. When the
// # user is inside a client workspace, setActiveEasychitConnectionId() routes those
// # calls through the backend registry proxy so Connection1/Connection2 headers are
// # injected. Direct localhost:5080 calls have empty appsettings and fail with
// # "Host can't be null".
const EASYCHIT_URL = process.env.REACT_APP_EASYCHIT_URL || 'http://localhost:5080';

let _activeEasychitConnectionId = null;

export function setActiveEasychitConnectionId(connectionId) {
  _activeEasychitConnectionId = connectionId || null;
}

export function getActiveEasychitConnectionId() {
  return _activeEasychitConnectionId;
}

// # A few /api/Change endpoints take the global schema as a query parameter
// # instead of reading the X-Global-Schema header the proxy injects. The client
// # workspace publishes the registry entry global schema here so those screens
// # do not each have to hard-code "GLOBAL".
let _activeGlobalSchema = null;

export function setActiveGlobalSchema(globalSchema) {
  _activeGlobalSchema = (globalSchema || '').trim() || null;
}

export function getActiveGlobalSchema() {
  return _activeGlobalSchema || 'GLOBAL';
}

// # Every EasyChit endpoint that writes to a *_log table records who made the
// # change as a bigint user_id, keyed to EasyChit's own employee master. ITSM's
// # employee_id (KIT500) and EasyChit's employee_code (KCE00001) are separate
// # namespaces that do not join, so the link is stored on the ITSM user as
// # easychit_contact_id. Until that is backfilled the default below (1) is sent
// # for every Supporting Tools screen.
// #
// # Who made the change is recorded either way: SaveData still logs loginName,
// # the timestamp and the typed reason alongside every one of these.
export const SUPPORTING_TOOLS_USER_ID = 1;

let _auditUserId = null;

/** Publish the signed-in operator's EasyChit contact_id (see ClientWorkspacePage). */
export function setAuditUserId(contactId) {
  const n = Number(contactId);
  _auditUserId = Number.isFinite(n) && n > 0 ? n : null;
}

export function getAuditUserId() {
  return _auditUserId ?? SUPPORTING_TOOLS_USER_ID;
}

// # The two spellings below are not interchangeable. ASP.NET Core matches JSON
// # property names case-insensitively, so userId/UserId bind to the same field,
// # but userId and user_id are different names — each endpoint gets the one its
// # own DTO declares. ipAddress and activity_type are deliberately absent from
// # both: the API fills the address from its own host and writes the activity
// # code (U/D/I) itself for each operation, so neither is accepted from here.
function auditCamel() {
  return { userId: getAuditUserId() };
}

function auditSnake() {
  return { user_id: getAuditUserId() };
}

function getChangeApiBase() {
  if (_activeEasychitConnectionId) {
    return `${BACKEND_URL}/api/connection-registry/${encodeURIComponent(_activeEasychitConnectionId)}/easychit`;
  }
  return `${EASYCHIT_URL}/api/Change`;
}

// Template strings like `${API}/GetBranchNames` call toString() on each evaluate.
const API = {
  toString() { return getChangeApiBase(); },
  valueOf() { return getChangeApiBase(); },
};

export async function getBranchNames() {
  const res = await axios.get(`${API}/GetBranchNames`, authHeaders());
  return res.data;
}

// The company configured for one CAO branch. The endpoint has always taken a
// branch_code — its query filters on branch_name ILIKE '%CAO%' AND branch_code —
// but nothing was passing one, so it could only ever match on a null branch.
export async function GetcompanyNames(branchCode) {
  const res = await axios.get(`${API}/GetcompanyNames`, {
    ...authHeaders(), params: { branch_code: branchCode },
  });
  return res.data;
}

export async function getbranchnames() {
  const res = await axios.get(`${API}/GetChequeBranchName`, authHeaders());
  return res.data;
}

export async function getGroupCode(branchschema) {
  const res = await axios.get(`${API}/GetGroupcodes`, {
    ...authHeaders(), params: { branchschema },
  });
  return res.data;
}

export async function getApprovedGroupCodes(branchSchema, branchCode) {
  const res = await axios.get(`${API}/GetApprovedGroupCodes`, {
    ...authHeaders(), params: { branchSchema, branchCode },
  });
  return res.data;
}

export async function getCommencedGroupCodes(branchschema, branchCode) {
  const res = await axios.get(`${API}/GetcommencedGroupcodes`, {
    ...authHeaders(), params: { branchschema, branch_code: branchCode },
  });
  return res.data;
}

export async function getTickets(schema, groupcode) {
  const res = await axios.get(`${API}/GetTickets`, {
    ...authHeaders(), params: { schema, groupcode },
  });
  return res.data;
}

export async function getTickets1(groupcode, branchcode, branchschema) {
  const res = await axios.get(
    `${API}/GetTickets1?groupcode=${groupcode}&branchcode=${branchcode}&branchschema=${branchschema}`,
    authHeaders(),
  );
  return res.data;
}

export async function getCommencedGroupTickets(branchschema, groupcode) {
  const res = await axios.get(`${API}/GetcomencedgroupcodeTickets`, {
    ...authHeaders(), params: { branchschema, groupcode },
  });
  return res.data;
}

export async function getByTicket(schema, ticketNo, chitgroupid) {
  const res = await axios.get(`${API}/GetOldNameByTicketNo`, {
    ...authHeaders(),
    params: { Schema: schema, ticketNo: String(ticketNo), chitgroupid: String(chitgroupid) },
  });
  return res.data;
}

// --- Name change ---

export async function updateRecordsnamechange(payload, branchschema) {
  const res = await axios.post(`${API}/Updatenamebyticketno`, { ...payload, ...auditSnake() }, {
    ...authHeaders(),
    params: { branchschema },
    headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
  });
  return res.data;
}

export async function UpdateMoblieNoByContact({ contact_id, newMobileNo, ptypeofoperation }) {
  const res = await axios.post(
    `${API}/UpdateMoblieNoByContact`,
    { contact_id, newMobileNo, ptypeofoperation, ...auditSnake() },
    authHeaders(),
  );
  return res.data;
}

export async function UpdateAddressByContact({
  contact_id, newAddress, newArea, newCity, newPincode, ptypeofoperation,
}) {
  const res = await axios.post(
    `${API}/UpdateAddressByContact`,
    {
      contact_id, newAddress, newArea, newCity, newPincode, ptypeofoperation,
      ...auditSnake(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Audit trail / reports ---

export async function saveAuditTrail(payload) {
  const res = await axios.post(`${API}/SaveData`, payload, authHeaders());
  return res.data;
}

export async function saveAuditlog(payload) {
  const res = await axios.post(`${API}/GetUpdateReports`, payload, authHeaders());
  return res.data;
}

export async function saveData(payload) {
  return saveAuditTrail(payload);
}

export async function saveAuditLogData(data) {
  const res = await axios.post(`${API}/SaveData`, data, authHeaders());
  return res.data;
}

export async function getUpdateReports(fromDate, toDate, changeType) {
  const res = await axios.get(`${API}/GetUpdateReports`, {
    ...authHeaders(), params: { fromdate: fromDate, todate: toDate, changetype: changeType },
  });
  return res.data;
}

export async function getChangeTypes() {
  const res = await axios.get(`${API}/GetChangeTypes`, authHeaders());
  return res.data;
}

export async function getReferralDetails(referralCode) {
  const res = await axios.get(`${API}/GetReferralDetails`, {
    ...authHeaders(), params: { referralCode },
  });
  return res.data;
}

export async function getAgentCodes() {
  const res = await axios.get(`${API}/Agentcode`, authHeaders());
  return res.data;
}

// Every referral under one agent unique code: agent_unique_code, referral_code,
// pan_number, transaction_date.
export async function getAgentByUniqueCode(agentuniquecode) {
  const res = await axios.get(`${API}/agentuniquecode`, {
    ...authHeaders(), params: { agentuniquecode },
  });
  return res.data;
}

export async function updateAgentBranch(payload) {
  const res = await axios.post(`${API}/UpdateAgentBranch`, { ...payload, ...auditSnake() }, {
    ...authHeaders(),
    headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
  });
  return res.data;
}

// --- Agent code delete ---

export async function getAgentCodesByBranch(branchConfigurationId) {
  // The API route is "getagentcodesbybranch". This had been written without the
  // leading g, so the call 404d and Agent Code Delete never loaded its codes.
  const res = await axios.get(`${API}/getagentcodesbybranch`, {
    ...authHeaders(), params: { tbl_mst_branch_configuration_id: branchConfigurationId },
  });
  return res.data;
}

export async function deleteAgentCode(referralcode) {
  // Controller always returns 200 with the DAL's result string ("Success" or an
  // error message) — callers must check the string itself, not rely on a thrown error.
  // GlobalSchema is left out: the action fills it from the X-Global-Schema header
  // when the body omits it.
  const res = await axios.post(
    `${API}/DeleteAgentAllocation`,
    { referralCode: referralcode, ...auditCamel() },
    { ...authHeaders(), responseType: 'text' },
  );
  return res.data;
}

// --- Auction schedule ---

export async function getAuctionSchedule(branchschema, chitgroupid) {
  const res = await axios.get(`${API}/get-auction-schedules`, {
    ...authHeaders(), params: { branchschema, chitgroupid: String(chitgroupid) },
  });
  return res.data;
}

// The update sets date, time, hall and conducted_id (the auctioner's contact_id) together.
export async function updateAuctionSchedule({
  branch_code, chitgroup_id, newauctiondate, newauctiontime, newauctionhall, contact_id,
}) {
  const res = await axios.post(
    `${API}/update-auction-schedule`,
    {
      branchschema: branch_code,
      newauctiondate,
      newauctiontime,
      newauctionhall: String(newauctionhall ?? ''),
      contact_id: Number(contact_id),
      chitgroupid: Number(chitgroup_id),
      ...auditSnake(),
    },
    authHeaders(),
  );
  return res.data;
}

export async function getAuctionNumbers(branchschema, chitgroupid) {
  const res = await axios.get(`${API}/get-auction-numbers`, {
    ...authHeaders(), params: { branchschema, chitgroupid: String(chitgroupid) },
  });
  return res.data;
}

export async function updateBidLossPermission(branchschema, auctionNo, ticketNo, chitgroupid) {
  // The controller declares chitgroupid as a non-nullable Int64 and the DAL
  // concatenates auction_number/ticketno straight into the UPDATE statement, so
  // every one of these has to reach the server as a real number.
  const auction = Number(auctionNo);
  const ticket = Number(ticketNo);
  const chitgroup = Number(chitgroupid);
  if (!branchschema || !Number.isFinite(auction) || !Number.isFinite(ticket) || !Number.isFinite(chitgroup)) {
    throw new Error('Bid loss permission needs a branch, auction number, ticket number and chit group id.');
  }
  const res = await axios.post(
    `${API}/update-bidloss-permission`,
    {
      branchschema,
      auction_number: String(auction),
      ticketno: String(ticket),
      chitgroupid: chitgroup,
      ...auditSnake(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Vacant status ---

export async function getVacantStatus() {
  const res = await axios.get(`${API}/getvacantstatus`, authHeaders());
  return res.data;
}

// `accept_vacant_receipt` is the screen's checkbox, not the DB column: true means
// vacant receipts are accepted, which the DAL stores as
// is_vacant_full_receipt_mandatory = false. The flag is required — the controller
// declares it bool? and answers 400 when absent, because a missing flag binding to
// false would revoke the permission rather than leave it alone.
export async function updateVacantStatus(branchCode, acceptVacantReceipt) {
  // A strict boolean test rather than coercion: axios drops undefined params, so
  // Boolean(undefined) would quietly send a revoke instead of failing here.
  if (!branchCode || typeof acceptVacantReceipt !== 'boolean') {
    throw new Error('Vacant receipt permission needs a branch code and an explicit accept flag.');
  }
  const res = await axios.post(
    `${API}/updatevacantstatus`,
    {
      branch_code: branchCode,
      accept_vacant_receipt: acceptVacantReceipt,
      ...auditSnake(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Contact / chit count ---

export async function getContactById(id) {
  const res = await axios.get(`${API}/getmaxchitcount?referenceid=${id}`, authHeaders());
  return res.data;
}

export async function getContactByMobile(mobile) {
  const res = await axios.get(`${API}/getmaxchitcount?contactno=${mobile}`, authHeaders());
  return res.data;
}

// updatemultiplecontact takes `referenceid` and `totalcount` as plain method
// parameters with no [FromBody], so ASP.NET Core binds them from the query
// string. Posting them as a JSON body left both at their defaults — null and 0 —
// and the update ran as `set max_chits_per_contact=0 where contact_reference_id=''`,
// which matches nothing. The endpoint still answered 200, so the screen reported
// success while the chit count never moved.
export async function updateTotalChits({ referenceid, totalcount }) {
  const res = await axios.post(
    `${API}/update-contact`,
    {
      // contact_id is on the DTO but nothing reads it: both the UPDATE and its
      // log row are selected by contact_reference_id.
      // Text, e.g. "CNT2622309". Number() turned it into NaN, which JSON sends as null.
      referenceid: String(referenceid ?? '').trim(),
      totalcount: Number(totalcount),
      ...auditSnake(),
    },
    authHeaders(),
  );
  return res.data;
}

export async function getsubscribername(branchschema, ticketno, groupcode, branch_code) {
  const res = await axios.get(
    `${API}/getsubscribername?branchschema=${branchschema}&ticketno=${ticketno}&groupcode=${groupcode}&branch_code=${branch_code}`,
    authHeaders(),
  );
  return res.data;
}

export async function deletelegalcell(groupcode, ticketno, isChecked) {
  // Still query-bound on the server; only the audit fields were added to it.
  // isChecked true = grant ('f' -> 't'), false = revoke ('t' -> 'f').
  const res = await axios.post(`${API}/deletelegalcell`, null, {
    ...authHeaders(),
    params: {
      groupcode,
      ticketno,
      isChecked: !!isChecked,
      UserId: getAuditUserId(),
    },
  });
  return res.data;
}

// --- Cheque return charges ---

export async function getchequesreturncharges(company_code) {
  const res = await axios.get(
    `${API}/getchequesreturncharges?company_code=${company_code}`,
    authHeaders(),
  );
  return res.data;
}

export async function updatechequesreturncharges(id, amount) {
  const res = await axios.post(
    `${API}/updatechequesreturncharges`,
    {
      tbl_mst_chit_company_configuration_id: Number(id),
      chequereturn_charges_amount: Number(amount),
      ...auditSnake(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Chit group dates change ---

export async function getChitGroupDetails(branchschema, groupcode) {
  const res = await axios.get(`${API}/get-dates-change`, {
    ...authHeaders(), params: { branchschema, groupcode },
  });
  return res.data;
}

export async function updateChitGroup(branchschema, payload) {
  const res = await axios.post(`${API}/UpdateDatesChangeDetails`, { ...payload, ...auditSnake() }, {
    ...authHeaders(),
    params: { branchschema },
    headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
  });
  return res.data;
}

// --- Approved details / first memo ---

export async function getApprovedDetails(groupcode, ticketno) {
  const res = await axios.get(`${API}/GetApprovedDetails`, {
    ...authHeaders(), params: { groupcode, ticketno },
  });
  return res.data;
}

export async function removeFirstMemo(groupCode, ticketNo) {
  const res = await axios.post(
    `${API}/RemoveFirstMemo`,
    { groupcode: groupCode, ticketno: Number(ticketNo), ...auditSnake() },
    authHeaders(),
  );
  return res.data;
}

// --- SJV ---

export async function getSjvNo(branchCode) {
  const res = await axios.get(`${API}/getsjvno?branchschema=${branchCode}`, authHeaders());
  return res.data;
}

export async function updateSjvStatus(branch_code, transaction_no) {
  const res = await axios.post(
    `${API}/UpdateSjvNo`,
    { transaction_no, ...auditSnake() },
    { ...authHeaders(), params: { BranchSchema: branch_code } },
  );
  return res.data;
}

// --- General receipts ---

export async function getGeneralReceiptNumbers(branchSchema, branchCode) {
  const res = await axios.get(`${API}/getgeneralreceiptnumbers`, {
    ...authHeaders(), params: { branchschema: branchSchema, branchcode: branchCode },
  });
  return res.data;
}

export async function getGeneralReceiptDetails(branchSchema, generalReceiptNumber, interbranchId) {
  const res = await axios.get(`${API}/getgeneralreceiptdetails`, {
    ...authHeaders(),
    params: {
      branchschema: branchSchema, general_receipt_number: generalReceiptNumber,
      interbranch_id: interbranchId,
    },
  });
  return res.data;
}

export async function getGeneralReceiptAmount(branchSchema, depositedReferenceNo) {
  const res = await axios.get(`${API}/getgeneralreceiptamount`, {
    ...authHeaders(),
    params: { branchschema: branchSchema, deposited_reference_no: depositedReferenceNo },
  });
  return res.data;
}

export async function getReceiptVoucherTransactions(branchcode, generalReceiptNumber, contactId) {
  const res = await axios.get(`${API}/GetReceiptVoucherTransactions`, {
    ...authHeaders(),
    params: { branchSchema: branchcode, generalReceiptNumber, contactId },
  });
  return res.data;
}

export async function reverseReceipt(
  branchschema, groupcode, ticketno, contactid, generalreceiptnumber,
  commonreceiptnumber, paymentnumber, paymentdate, paymentvoucherid,
  paymentvoucherdetailsid, caoname,
) {
  const res = await axios.post(
    `${API}/ReverseReceipt`,
    {
      caoschema: caoname ?? '',
      branchschema,
      groupcode,
      ticketno: Number(ticketno),
      contactid: Number(contactid),
      generalreceiptnumber,
      commonreceiptnumber: Number(commonreceiptnumber),
      paymentnumber,
      paymentdate,
      paymentvoucherid: Number(paymentvoucherid),
      paymentvoucherdetailsid: Number(paymentvoucherdetailsid),
      ...auditSnake(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Cheque general receipt numbers ---

export async function getChequeGeneralReceiptNumbers(branchSchema, branchCode) {
  const res = await axios.get(`${API}/getchequegeneralnumbers`, {
    ...authHeaders(), params: { branchschema: branchSchema, branchcode: branchCode },
  });
  return res.data;
}

export async function getChequeGeneralReceiptNumberDetails(branchSchema, general_receipt_number) {
  const res = await axios.get(`${API}/getchequegeneralnumbersdetails`, {
    ...authHeaders(), params: { branchschema: branchSchema, general_receipt_number },
  });
  return res.data;
}

export async function getChequeLiveDetails(general_receipt_number, branchschema, interbranch_id, reference_number) {
  // interbranch_id binds to a non-nullable long.
  const interbranch = Number(interbranch_id);
  const res = await axios.get(`${API}/GetchequeliveDetails`, {
    ...authHeaders(),
    params: {
      general_receipt_number, branchschema,
      interbranch_id: Number.isFinite(interbranch) ? interbranch : 0,
      reference_number: reference_number ?? '',
    },
  });
  return res.data;
}

export async function reverseChequeDeposit(
  caoschema, branchschema, chitgroupid, ticketno, contactid,
  trimnumber, referencenumber, receiptnumber, bankentriesid, transactionno,
) {
  // chitgroupid / ticketno / contactid / bankentriesid are non-nullable longs on
  // the controller; axios drops null and undefined params, which would turn a
  // half-loaded receipt into an opaque 400.
  const nums = {
    chitgroupid: Number(chitgroupid),
    ticketno: Number(ticketno),
    contactid: Number(contactid),
    bankentriesid: Number(bankentriesid),
  };
  const missing = Object.entries(nums).filter(([, v]) => !Number.isFinite(v)).map(([k]) => k);
  if (missing.length) {
    throw new Error(`Cheque reversal is missing numeric value(s): ${missing.join(', ')}.`);
  }
  const res = await axios.post(
    `${API}/ReverseChequeDeposit`,
    {
      caoSchema: caoschema ?? '',
      branchSchema: branchschema,
      chitGroupId: nums.chitgroupid,
      ticketNo: nums.ticketno,
      contactId: nums.contactid,
      bankEntriesId: nums.bankentriesid,
      trimNumber: trimnumber ?? '',
      referenceNumber: referencenumber ?? '',
      receiptNumber: receiptnumber ?? '',
      transactionNo: transactionno ?? '',
      ...auditCamel(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Cheque details / clearing ---

export async function getcheckdetails(branchSchema, checkNo) {
  const res = await axios.get(`${API}/GetChequeDetails`, {
    ...authHeaders(), params: { branchschema: branchSchema, checkreferno: checkNo },
  });
  return res.data;
}

export async function updateClearDate(
  branchSchema, checkReferNo, paymentNo,
) {
  const res = await axios.post(
    `${API}/ClearDateandStatus`,
    {
      branchSchema,
      referenceNumber: checkReferNo,
      paymentNumber: paymentNo,
      transactionNo: '',
      bankEntriesId: 0,
      clearStatus: '',
      ...auditCamel(),
    },
    authHeaders(),
  );
  return res.data;
}

export async function getCommencedChequeDetails(branchschema, groupcode, branchCode, ticketNo) {
  const res = await axios.get(`${API}/Getcomencedchequenumberdetails`, {
    ...authHeaders(),
    params: {
      branchschema, groupcode, branch_code: branchCode, tickeno: String(ticketNo),
    },
  });
  return res.data;
}

export async function deleteCheques(branchschema, chequenumbers, chequeids) {
  const res = await axios.post(
    `${API}/DeletePSCheques`,
    { chequenumbers, chequeids, ...auditSnake() },
    { ...authHeaders(), params: { BranchSchema: branchschema } },
  );
  return res.data;
}

export async function getEmployeeName(searchtype, companyCode = '', branchCode = '', LocalSchema = '', GlobalSchema = '') {
  const res = await axios.get(`${API}/GetSubInterducedDetails`, {
    ...authHeaders(),
    params: { LocalSchema, searchtype, companyCode, branchCode, GlobalSchema },
  });
  return res.data;
}

// --- MV numbers ---

export async function getMvNumbers(branchschema, branchcode) {
  const res = await axios.get(`${API}/GetMvNumbers`, {
    ...authHeaders(), params: { branchschema, branchcode },
  });
  return res.data;
}

export async function getMvDetails(branchschema, branchcode, payment_number) {
  const res = await axios.get(`${API}/GetMvdetails`, {
    ...authHeaders(),
    params: { branchschema, paymentnumber: payment_number, branchcode },
  });
  return res.data;
}

export async function getMvNumberDetails(branchschema, branchcode, paymentnumber, paymentvoucherid) {
  const res = await axios.get(`${API}/GetMvnumberdetails`, {
    ...authHeaders(),
    params: { branchschema, branchcode, paymentnumber, paymentvoucherid },
  });
  return res.data;
}

export async function reverseMvVoucher(
  branchschema, payment_number, payment_date, paymentvoucherid, contactid, paymentvoucherdetailsid,
) {
  const res = await axios.post(
    `${API}/ReverseMvVoucher`,
    {
      branchSchema: branchschema,
      paymentNumber: payment_number,
      paymentDate: payment_date,
      paymentVoucherId: Number(paymentvoucherid),
      contactId: Number(contactid),
      paymentVoucherDetailsId: Number(paymentvoucherdetailsid),
      ...auditCamel(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Business reference ---

export async function getBusinessRefByTicket(schema, ticketNo, chitgroupId) {
  const res = await axios.get(`${API}/GetBusinessRefByTicket`, {
    ...authHeaders(),
    params: { Schema: schema, ticketNo: String(ticketNo), chitgroupid: String(chitgroupId) },
  });
  return res.data;
}

export async function updateBusinessRef(payload, branchSchema) {
  const res = await axios.post(`${API}/UpdateBusinessRef`, { ...payload, ...auditSnake() }, {
    ...authHeaders(),
    params: { branchschema: branchSchema },
    headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
  });
  return res.data;
}

// --- Agent to direct (minimum 1% subscriber details) ---

export async function getMinimumOnePercentSubscriberDetails(
  branchSchema, groupcode, ticketno, branchCode, globalSchema,
) {
  const res = await axios.get(`${API}/GetMinimumOnePercentSubscriberDetails`, {
    ...authHeaders(),
    params: {
      branchschema: branchSchema,
      globalschema: globalSchema || getActiveGlobalSchema(),
      groupcode,
      ticketno: Number(ticketno),
      branchCode: branchCode || branchSchema,
    },
  });
  return res.data;
}

// Re-points the subscriber's introducer to DIRECT and deletes the agent
// commission, TDS voucher and total-transaction rows behind it. Destructive and
// not reversible from the UI — the caller gates it behind an explicit tick.
//
// paymentVoucherNumber and transactionNo are interpolated into SQL `in(...)`
// lists server-side, so a comma-separated list covers a ticket with several
// commission rows.
export async function updateAgentToDirect({
  branchSchema, interBranchSchema, chitgroupId, ticketno,
  contactId, commissionContactId, paymentVoucherNumber, transactionNo,
  transactionDate, groupcode,
}) {
  const res = await axios.post(`${API}/Updateagenttodirect`, null, {
    ...authHeaders(),
    params: {
      branchschema: branchSchema,
      interbranchschema: interBranchSchema || branchSchema,
      chitgroup_id: Number(chitgroupId),
      ticketno: Number(ticketno),
      contact_id: Number(contactId),
      commission_contact_id: Number(commissionContactId),
      payment_voucher_number: paymentVoucherNumber,
      transaction_no: transactionNo,
      transaction_date: transactionDate,
      groupcode,
      UserId: getAuditUserId(),
    },
  });
  return res.data;
}

// Re-points the subscriber's introducer from one agent to another and clears the
// commission paperwork behind the old one. Destructive and not reversible from
// the UI — the caller gates it behind an explicit tick.
//
// introducedId is the NEW agent's contact id (contactId on the Agentcode list).
// commissionTransactionNumber is matched with `=` server-side, not `in(...)`, so
// it takes a single value while the voucher / transaction params take lists.
export async function updateAgentToAgent({
  branchschema, interBranchSchema, chitgroupId, ticketno, contactId, introducedId,
  commencedChitgroupId, commencedTicketno, paymentVoucherNumber, commissionVoucherNo,
  commissionTransactionNumber, transactionNo,
}) {
  const res = await axios.post(`${API}/Updateagenttoagent`, null, {
    ...authHeaders(),
    params: {
      branchschema,
      interbranchschema: interBranchSchema || branchschema,
      chitgroup_id: Number(chitgroupId),
      ticketno: Number(ticketno),
      contact_id: Number(contactId),
      introduced_id: Number(introducedId),
      commenced_chitgroup_id: Number(commencedChitgroupId ?? chitgroupId),
      commenced_ticketno: Number(commencedTicketno ?? ticketno),
      payment_voucher_number: paymentVoucherNumber,
      commission_voucher_no: commissionVoucherNo,
      commission_transaction_number: commissionTransactionNumber,
      transaction_no: transactionNo,
      UserId: getAuditUserId(),
    },
  });
  return res.data;
}

// Employee master, used by Agent To SubID's New Employee Code picker and by
// Business Reference Change's New Employee Reference ID picker. Comes back as
// [{ employee_code, contact_id, business_reference_id, business_reference_name,
// branch_name }]. contact_id is what Agent To SubID writes into subintroduced_id
// and, identically, what Business Reference Change writes as the new
// business_reference_id — that column holds the employee's contact_id, so
// business_reference_id is returned as an alias of contact_id, not a separate key.
export async function getEmployeeCodes() {
  const res = await axios.get(`${API}/getemployeecodes`, authHeaders());
  return res.data;
}

// Re-points the subscriber onto a sub ID: the agent stays as it is and the
// selected employee becomes the sub-introducer. Destructive and not reversible
// from the UI — it deletes the ticket's agent commission, TDS voucher and
// total-transaction rows, then regenerates the commission via fn_agentcommission.
//
// paymentVoucherNumber and transactionNo are interpolated into SQL `in(...)`
// lists server-side, so a comma-separated list covers a ticket with several rows.
export async function updateAgentToSubId({
  branchschema, interBranchSchema, chitgroupId, ticketno, contactId,
  introducedId, subintroducedId, paymentVoucherNumber, transactionNo,
  transactionDate, groupcode,
}) {
  const res = await axios.post(`${API}/Updateagenttosubid`, null, {
    ...authHeaders(),
    params: {
      branchschema,
      interbranchschema: interBranchSchema || branchschema,
      chitgroup_id: Number(chitgroupId),
      ticketno: Number(ticketno),
      contact_id: Number(contactId),
      introduced_id: Number(introducedId),
      subintroduced_id: Number(subintroducedId),
      payment_voucher_number: paymentVoucherNumber,
      transaction_no: transactionNo,
      transaction_date: transactionDate,
      groupcode,
      UserId: getAuditUserId(),
    },
  });
  return res.data;
}

// The subscriber behind a ticket: [{ subscriber_name, contact_id }]. Change Name
// uses it to name the member and to get the contact id its detail grid is keyed on.
// `branchschema` is matched against branch_code server-side, so the branch code is
// what goes here.
export async function getSubscriberNames(branchschema, groupcode, ticketno) {
  const res = await axios.get(`${API}/getnames`, {
    ...authHeaders(),
    params: { branchschema, groupcode, ticketno: Number(ticketno) },
  });
  return res.data;
}

// Every chit held by one contact: [{ contact_id, subscriber_name, groupcode,
// ticketno, chit_status }]. The route really is spelled "getnamesdetials" on the
// API side — a pre-existing typo, not one to fix here without changing the server.
export async function getSubscriberNameDetails(contactId) {
  const res = await axios.get(`${API}/getnamesdetials`, {
    ...authHeaders(),
    params: { contact_id: Number(contactId) },
  });
  return res.data;
}

/** Every address row on file for a contact — the Mobile No & Address Change grid. */
export async function getAddressDetails(contactId) {
  const res = await axios.get(`${API}/getaddressdetails`, {
    ...authHeaders(),
    params: { contact_id: Number(contactId) },
  });
  return res.data;
}

// --- Cheque numbers / clear status ---

export async function getChequenumbers(branchschema) {
  const res = await axios.get(`${API}/GetChequenumbers`, {
    ...authHeaders(), params: { branchschema },
  });
  return res.data;
}

export async function getChequeDetails(branchschema, paymentnumber, referencenumber, clearstatus) {
  const res = await axios.get(`${API}/GetchequeDetails`, {
    ...authHeaders(), params: { branchschema, paymentnumber, referencenumber, clearstatus },
  });
  return res.data;
}

export async function clearDateAndStatus(
  branchschema, paymentnumber, referencenumber, transactionno, bankentriesid, clearstatus,
) {
  // bankentriesid binds to a non-nullable long; rows with clear_status 'P' carry
  // no bank entry at all, so normalise the missing case to 0 rather than letting
  // axios drop the parameter and fail model binding.
  const bankEntry = Number(bankentriesid);
  const res = await axios.post(
    `${API}/ClearDateandStatus`,
    {
      branchSchema: branchschema,
      paymentNumber: paymentnumber,
      referenceNumber: referencenumber,
      transactionNo: transactionno ?? '',
      bankEntriesId: Number.isFinite(bankEntry) ? bankEntry : 0,
      clearStatus: clearstatus,
      ...auditCamel(),
    },
    { ...authHeaders(), responseType: 'text' },
  );
  return res.data;
}

// --- PAN change ---

export async function getPANList() {
  const res = await axios.get(`${API}/Getpannumbers`, authHeaders());
  return res.data;
}

export async function getContactDocumentDetails(panNumber) {
  const res = await axios.get(`${API}/GetContactDocumentDetails`, {
    ...authHeaders(), params: { panNumber },
  });
  return res.data;
}

export async function updatePanNumber({
  branchschema, oldPanNumber, newPanNumber, contactReferenceId, contactId, documentIds,
}) {
  // contactId binds to a non-nullable long and is spliced into an IN(...) clause.
  const contact = Number(contactId);
  if (!Number.isFinite(contact)) {
    throw new Error('PAN change needs a numeric contact id.');
  }
  const res = await axios.post(
    `${API}/UpdatePanNumber`,
    {
      branchSchema: branchschema ?? '',
      oldPanNumber,
      newPanNumber,
      // The DTO pluralised this — it is spliced into an IN(...) list.
      contactReferenceIds: contactReferenceId ?? '',
      contactId: contact,
      documentIds: documentIds ?? '',
      ...auditCamel(),
    },
    { ...authHeaders(), responseType: 'text' },
  );
  return res.data;
}

// --- Payment adjustment ---

export async function getPaymentAdjustmentDetails(branchschema, groupcode, ticketno) {
  const res = await axios.get(`${API}/GetPaymentAdjustmentDetails`, {
    ...authHeaders(), params: { branchschema, groupcode, ticketno },
  });
  return res.data;
}

export async function getSubscriberDetails(branchschema, groupcode, ticketno) {
  const res = await axios.get(`${API}/GetSubscriberDetails`, {
    ...authHeaders(), params: { branchschema, groupcode, ticketno },
  });
  return res.data;
}

export async function insertSubscriberIncome(branchschema, subscriberId, branch_id) {
  const res = await axios.post(
    `${API}/InsertSubscriberIncome`,
    {
      branchSchema: branchschema,
      subscriberId: Number(subscriberId),
      branchId: Number(branch_id),
      ...auditCamel(),
    },
    authHeaders(),
  );
  return res.data;
}

export async function updateAdjustmentDetails(payload) {
  const res = await axios.post(
    `${API}/UpdateAdjustmentDetails`,
    { ...payload, ...auditCamel() },
    authHeaders(),
  );
  return res.data;
}

// --- Removed Chit Live ---

// Endpoint names are provisional until the backend routes are supplied.
export async function getRemovedChitLiveDetails(branchschema, groupcode, ticketno) {
  const res = await axios.get(`${API}/GetRemovedChitLiveDetails`, {
    ...authHeaders(), params: { branchschema, groupcode, ticketno },
  });
  return res.data;
}

export async function deleteRemovedChitLive(payload) {
  const res = await axios.post(
    `${API}/DeleteRemovedChitLive`,
    { ...payload, ...auditCamel() },
    { ...authHeaders(), responseType: 'text' },
  );
  return res.data;
}

// --- Reauction ---

// ticketno is an optional filter. Leaving it out returns every reauctioned ticket
// in the group, which is what the From Ticket No dropdown is built from; passing
// one narrows those rows to that ticket's subscriber details.
export async function getReauctionSubscriberDetails(branchschema, groupcode, ticketno, branch_code) {
  const hasTicket = ticketno !== null && ticketno !== undefined && ticketno !== '';
  const res = await axios.get(`${API}/GetreauctionSubscriberDetails`, {
    ...authHeaders(),
    params: {
      branchschema, groupcode, branch_code, ...(hasTicket ? { ticketno: Number(ticketno) } : {}),
    },
  });
  return res.data;
}

export async function getReauctionDetails(branchschema, from_ticketno, to_ticketno) {
  // from_ticketno/to_ticketno bind to non-nullable ints. axios drops null and
  // undefined params entirely, which turns a missing ticket into a 400 rather
  // than an empty result — so bail out here with something readable instead.
  const from = Number(from_ticketno);
  const to = Number(to_ticketno);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return [];
  const res = await axios.get(`${API}/GetreauctionDetails`, {
    ...authHeaders(), params: { branchschema, from_ticketno: from, to_ticketno: to },
  });
  return res.data;
}

export async function getReauctionDividendDetails(branchschema, transaction_no) {
  const res = await axios.get(`${API}/Getreauctiondivdenddetails`, {
    ...authHeaders(), params: { branchschema, transaction_no },
  });
  return res.data;
}

export async function updateReauctionTransactionDates({
  branchschema, chitgroupid, toticketno, ticketno, reauctiondate, oldtransactiondate,
  createddate, subscriberjvno, dividendtransactionnos, bidjvtransactionnos,
}) {
  // chitgroupid / toticketno / ticketno bind to non-nullable longs; axios omits
  // null and undefined params, so a half-loaded ticket would 400 on the server
  // instead of failing here with something the screen can explain.
  const chitgroup = Number(chitgroupid);
  const toTicket = Number(toticketno);
  const ticket = Number(ticketno);
  if (!branchschema || !Number.isFinite(chitgroup) || !Number.isFinite(toTicket) || !Number.isFinite(ticket)) {
    throw new Error('Reauction update needs a branch, chit group id and both ticket numbers.');
  }
  const res = await axios.post(
    `${API}/UpdateReauctionTransactionDates`,
    {
      branchSchema: branchschema,
      chitGroupId: chitgroup,
      toTicketNo: toTicket,
      ticketNo: ticket,
      reauctionDate: reauctiondate,
      oldTransactionDate: oldtransactiondate ?? '',
      createdDate: createddate,
      subscriberJvNo: subscriberjvno ?? '',
      dividendTransactionNos: dividendtransactionnos ?? '',
      bidJvTransactionNos: bidjvtransactionnos ?? '',
      // Declared on the DTO but never read by the reauction DAL method.
      auctionNumber: 0,
      ...auditCamel(),
    },
    { ...authHeaders(), responseType: 'text' },
  );
  return res.data;
}

// --- Guarantor name change ---

export async function getGuarantorNameChangeDetails(branchschema, groupcode, ticketno, branch_code) {
  const res = await axios.get(`${API}/GetnamechangeDetails`, {
    ...authHeaders(), params: { branchschema, groupcode, ticketno, branch_code },
  });
  return res.data;
}

export async function updateSubscriberGuarantorName(
  branchschema, chitgroupid, ticketno, contactid, contactname, contactmailingname, contactsurname,
) {
  const res = await axios.post(
    `${API}/UpdateSubscriberGuarantorName`,
    {
      branchSchema: branchschema,
      chitGroupId: Number(chitgroupid),
      ticketNo: Number(ticketno),
      contactId: Number(contactid),
      contactName: contactname,
      contactMailingName: contactmailingname,
      contactSurname: contactsurname,
      ...auditCamel(),
    },
    { ...authHeaders(), responseType: 'text' },
  );
  return res.data;
}

// --- Guarantor MVO ---

export async function getGuarantormvoDetails(branchschema, branchCode, groupcode, ticketNo) {
  const res = await axios.get(`${API}/GetGuarantormvoDetails`, {
    ...authHeaders(),
    params: {
      BranchSchema: branchschema, BranchCode: branchCode, GroupCode: groupcode,
      TicketNo: String(ticketNo),
    },
  });
  return res.data;
}

export async function getMvoReferenceIds(guarantorId, branchschema) {
  const res = await axios.get(`${API}/GetMVOReferenceIds`, {
    ...authHeaders(),
    params: { GuarantorIds: String(guarantorId), branchSchema: branchschema },
  });
  return res.data;
}

export async function deleteGuarantorDetails(
  branchschema, chitGroupId, ticketNo, contactIds, guarantorMVOIds,
) {
  const res = await axios.post(
    `${API}/DeleteGuarantorDetails`,
    {
      branchSchema: branchschema,
      chitGroupId: Number(chitGroupId),
      ticketNo: Number(ticketNo),
      contactIds,
      guarantorMVOIds,
      ...auditCamel(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Guarantor BPO ---

export async function getGuarantorbpoDetails(branchschema, branchCode, groupcode, ticketNo) {
  const res = await axios.get(`${API}/GetGuarantorbpoDetails`, {
    ...authHeaders(),
    params: {
      BranchSchema: branchschema, BranchCode: branchCode, GroupCode: groupcode,
      TicketNo: String(ticketNo),
    },
  });
  return res.data;
}

export async function deletebpoGuarantorDetails(
  branchschema, chitGroupId, ticketNo, contactIds, guarantorMVOIds,
) {
  const res = await axios.post(
    `${API}/DeletepdoGuarantorDetails`,
    {
      branchSchema: branchschema,
      chitGroupId: Number(chitGroupId),
      ticketNo: Number(ticketNo),
      contactIds,
      guarantorMVOIds,
      ...auditCamel(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Final settlement ---

export async function getFinalSettlement(branchSchema, groupCode, ticketNo) {
  const res = await axios.get(`${API}/Getfinalsetlement`, {
    ...authHeaders(),
    params: { BranchSchema: branchSchema, GroupCode: groupCode, TicketNo: ticketNo },
  });
  return res.data;
}

export async function getFinalSettlementDetails(suretyName, groupCode, ticketNo) {
  const res = await axios.get(`${API}/Getfinalsetlementdetails`, {
    ...authHeaders(),
    params: { surety_name: suretyName, groupCode, ticketNo },
  });
  return res.data;
}

export async function deleteFinalMemoDetails(
  branchSchema, chitGroupId, ticketNo, groupCode,
) {
  // globalSchema is omitted: the action falls back to the X-Global-Schema header.
  const res = await axios.post(
    `${API}/DeleteFinalMemoDetails`,
    {
      branchSchema,
      chitGroupId: Number(chitGroupId),
      ticketNo: Number(ticketNo),
      groupCode,
      ...auditCamel(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Chit pledge / security lien ---

export async function getChitPledgeCancelList(branchSchema, groupCode, ticketNo) {
  const res = await axios.get(`${API}/Getchitpledgecancel`, {
    ...authHeaders(),
    params: { BranchSchema: branchSchema, GroupCode: groupCode, TicketNo: ticketNo },
  });
  return res.data;
}

export async function deleteChitPledgeAndSecurityLienDetails(
  branchSchema, psGroupCode, psTicketNo, npsGroupCode,
) {
  const res = await axios.post(
    `${API}/DeleteChitPledgeAndSecurityLienDetails`,
    {
      branchSchema,
      psGroupCode,
      psTicketNo: Number(psTicketNo),
      npsGroupCode,
      ...auditCamel(),
    },
    authHeaders(),
  );
  return res.data;
}

// --- Surety ---

export async function getSuretyName(branchSchema, branchCode, groupCode, ticketNo) {
  const res = await axios.get(`${API}/Getsuretyname`, {
    ...authHeaders(),
    params: {
      BranchSchema: branchSchema, BranchCode: branchCode, GroupCode: groupCode,
      TicketNo: String(ticketNo),
    },
  });
  return res.data;
}

export async function getSuretyNameDetails(groupCode, ticketNo) {
  const res = await axios.get(`${API}/Getsuretynamedetails`, {
    ...authHeaders(), params: { GroupCode: groupCode, TicketNo: String(ticketNo) },
  });
  return res.data;
}

export async function deleteMvoSvoSuretyDetails(
  branchSchema, groupCode, ticketNo, mvoSuretyId, chitGroupId,
) {
  const res = await axios.post(`${API}/DeleteMvoSvoSuretyDetails`, null, {
    ...authHeaders(),
    params: {
      branchSchema, groupCode, ticketNo: String(ticketNo),
      mvoSuretyId: String(mvoSuretyId), chitGroupId: String(chitGroupId),
      UserId: getAuditUserId(),
    },
    responseType: 'text',
  });
  return res.data;
}

// --- Received documents / KGMS ---

export async function getReceivedDocument(branchSchema, groupCode, ticketNo) {
  const res = await axios.get(`${API}/GetRECEIVEDDOCUMENT`, {
    ...authHeaders(),
    params: { BranchSchema: branchSchema, GroupCode: groupCode, TicketNo: ticketNo },
  });
  return res.data;
}

export async function deleteKgmsMvoSvoDetails(
  branchSchema, groupCode, ticketNo, fileDownloadStatus,
) {
  const res = await axios.post(`${API}/DeleteKgmsMvoSvoDetails`, null, {
    ...authHeaders(),
    params: {
      branchschema: branchSchema, groupcode: groupCode, ticketno: ticketNo,
      filedownloadstatus: fileDownloadStatus,
      UserId: getAuditUserId(),
    },
    responseType: 'text',
  });
  return res.data;
}

// --- Central office chits ---

export async function getCentralOfficeChitDetails(branchSchema, branchCode, groupCode, ticketNo) {
  const res = await axios.get(`${API}/Getcentralofficechitsdetails`, {
    ...authHeaders(),
    params: {
      BranchSchema: branchSchema, BranchCode: branchCode, GroupCode: groupCode, TicketNo: ticketNo,
    },
  });
  return res.data;
}

export async function getCoBranchNames() {
  const res = await axios.get(`${API}/GetcoBranchNames`, authHeaders());
  return res.data;
}

export async function updateAdvanceInterestPaymentBank(branchSchema, payload) {
  const res = await axios.post(
    `${API}/UpdateAdvanceInterestPaymentBank`,
    { ...payload, ...auditCamel() },
    { ...authHeaders(), params: { BranchSchema: branchSchema } },
  );
  return res.data;
}

// --- Employee biometric attendance ---

export async function getBiometricEmployeeCodes(branchSchema) {
  const res = await axios.get(`${API}/Getbiometricemployeecodes`, {
    ...authHeaders(), params: { branchSchema },
  });
  return res.data;
}

export async function getBiometricFinancialYears(branchSchema, employeecode) {
  const res = await axios.get(`${API}/Getfinancialyear`, {
    ...authHeaders(), params: { branchSchema, employeecode },
  });
  return res.data;
}

export async function getBiometricCalendarMonths(branchSchema, employeecode, financialyear) {
  const res = await axios.get(`${API}/Getcalendermonth`, {
    ...authHeaders(), params: { branchSchema, employeecode, financialyear },
  });
  return res.data;
}

export async function getBiometricEmployeeDetails(
  branchSchema, employeeCode, financialYear, calendarMonth, attendanceDate,
) {
  const res = await axios.get(`${API}/Getbiometricemployeedetails`, {
    ...authHeaders(),
    params: { branchSchema, employeeCode, financialYear, calendarMonth, attendanceDate },
  });
  return res.data;
}

export async function updateEmployeeBiometricAttendance(
  branchSchema, employeeCode, leaveType, attendanceDate,
) {
  // attendanceDate binds to a DateTime — send the plain yyyy-MM-dd the grid holds.
  const res = await axios.post(
    `${API}/UpdateEmployeeBiometricAttendance`,
    {
      branchSchema, employeeCode, leaveType, attendanceDate, ...auditCamel(),
    },
    { ...authHeaders(), responseType: 'text' },
  );
  return res.data;
}

// --- Chit advance interest / TDS percentage ---

// The journal vouchers for a ticket on one chit receipt date (yyyy-MM-dd).
// The date is required; the server returns nothing without it.
export async function getJournalVoucherNumbers(branchschema, groupcode, ticketno, transactiondate) {
  const res = await axios.get(`${API}/getjournalvouchernumbers`, {
    ...authHeaders(), params: { branchschema, groupcode, ticketno, transactiondate },
  });
  return res.data;
}

export async function getJournalVoucherDetails(effected_jv_number, branchschema) {
  const res = await axios.get(`${API}/getjournalvoucherdetails`, {
    ...authHeaders(), params: { effected_jv_number, branchschema },
  });
  return res.data;
}

export async function updateSubscriberInterestAndTds(payload) {
  // Still query-bound on the server.
  const res = await axios.post(`${API}/UpdateSubscriberInterestAndTDS`, null, {
    ...authHeaders(),
    params: { ...payload, UserId: getAuditUserId() },
  });
  return res.data;
}

// --- Chit group cancel ---

// updatechitadvancestatus is query-bound on the server and only acts when
// ptypeofoperation is UPDATE and chitadvancestatus is COMPLETED.
export async function cancelChitGroup({
  branchSchema, chitgroupId, ticketNo, chitAdvanceStatus,
}) {
  const res = await axios.post(`${API}/updatechitadvancestatus`, null, {
    ...authHeaders(),
    params: {
      BranchSchema: branchSchema,
      chitgroupid: Number(chitgroupId),
      ticketno: Number(ticketNo),
      ptypeofoperation: 'UPDATE',
      chitadvancestatus: chitAdvanceStatus,
      UserId: getAuditUserId(),
    },
  });
  return res.data;
}

// ── Screen placement (Supporting Tools ⇄ Supporting Tools Live) ──────────────
export async function fetchScreenPlacement() {
  const res = await axios.get(`${REGISTRY_API}/screen-placement`, authHeaders());
  return res.data;
}

export async function moveScreens(screenKeys, target = 'live') {
  const res = await axios.put(
    `${REGISTRY_API}/screen-placement`,
    { screen_keys: screenKeys, target },
    authHeaders(),
  );
  return res.data;
}
